import React, { useEffect, useState } from 'react';
import driverApi from '../../clients/DriverClient';
import { type RaceAroundWorldProgress } from '../../models/RaceAroundWorldProgress';
import { formatDateOnlyLocal } from '../../utils/time';
import StarRating from '../shared/StarRating';
import ProgressIndicators from '../shared/ProgressIndicators';
import ExpandableCardList from '../shared/ExpandableCardList';

interface RaceAroundWorldViewProps {
  driverId: string | undefined;
}

const RaceAroundWorldView: React.FC<RaceAroundWorldViewProps> = ({ driverId }) => {
  const [progress, setProgress] = useState<RaceAroundWorldProgress[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  useEffect(() => {
    const fetchProgress = async () => {
      if (!driverId) return;
      setIsLoading(true);
      setError('');
      try {
        const result = await driverApi.getRaceAroundWorldProgress(driverId);
        if (result) {
          setProgress(result);
          const firstLevelWithProgress = result.findIndex(r => r.steps.some(s => s.stars > 0 ) && !r.steps.every(s => s.stars === 5));
          if (firstLevelWithProgress !== -1) {
            setExpandedItems(new Set([firstLevelWithProgress]));
          }
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load Race Around the World progress');
      } finally {
        setIsLoading(false);
      }
    };
    fetchProgress();
  }, [driverId]);

  const toggleExpanded = (index: number) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const getLevelProgress = (steps: any[]) => {
    const completed = steps.filter(s => s.stars > 0).length;
    return `${completed}/${steps.length}`;
  };

  const renderProgressIndicator = (level: RaceAroundWorldProgress) => {
    const completed = level.steps.filter(s => s.stars > 0).length;
    return (
      <>
        <div className="d-flex align-items-center gap-1">
          <ProgressIndicators completed={completed} total={level.steps.length} />
        </div>
        <span className="ms-1 text-white" style={{ fontSize: '1rem' }}>
          {getLevelProgress(level.steps)}
        </span>
      </>
    );
  };

  const renderTableRow = (level: RaceAroundWorldProgress) => (
    <>
      {level.steps.map((step, sIdx) => (
        <tr key={sIdx}>
          <td className="text-white" style={{ fontSize: '0.9rem' }}>{step.trackName}</td>
          <td className="text-white" style={{ fontSize: '0.9rem' }}>{step.carName}</td>
          <td className="text-white hide-mobile" style={{ fontSize: '0.9rem' }}>
            {step.date ? formatDateOnlyLocal(step.date) : '-'}
          </td>
          <td>
            <StarRating stars={step.stars} />
          </td>
        </tr>
      ))}
    </>
  );

  return (
    <ExpandableCardList
      data={progress}
      isLoading={isLoading}
      error={error}
      emptyMessage="You don't have any progress yet."
      expandedItems={expandedItems}
      onToggleExpanded={toggleExpanded}
      renderTitle={(level) => level.name}
      renderProgressIndicator={renderProgressIndicator}
      tableHeaders={[
        { label: 'Track' },
        { label: 'Car' },
        { label: 'Last Attempt', hideMobile: true },
        { label: 'Stars' }
      ]}
      renderTableRow={renderTableRow}
    />
  );
};

export default RaceAroundWorldView;
